/** Standardised format for coordinate values with supported converters and validation. */
export default class Coordinate {
  private static validate(latitude: number, longitude: number) {
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude))
      throw new Error("Invalid input: unable to parse latitude or longitude");

    if (latitude < -90 || latitude > 90)
      throw new Error(
        `Invalid latitude value: ${latitude} is outside the range [-90, 90]`,
      );

    if (longitude < -180 || longitude > 180)
      throw new Error(
        `Invalid longitude value: ${longitude} is outside the range [-180, 180]`,
      );
  }

  // -------------------------------------------------------------------- //
  // ------------------------------- State ------------------------------ //
  // -------------------------------------------------------------------- //

  /** The latitude for this point. */
  latitude: number;
  /** The longitude for this point. */
  longitude: number;

  // -------------------------------------------------------------------- //
  // --------------------------- Constructor ---------------------------- //
  // -------------------------------------------------------------------- //

  constructor({
    latitude,
    longitude,
  }: {
    /** The latitude for this point. */
    latitude: number;
    /** The longitude for this point. */
    longitude: number;
  }) {
    Coordinate.validate(latitude, longitude);
    this.latitude = latitude;
    this.longitude = longitude;
  }

  /**
   * Parses latitude and longitude from an unparsed object and creates a Coordinate instance if both are successfully parsed.
   *
   * @param {Object} obj - An object containing latitude and longitude as strings.
   * @return {Coordinate} A Coordinate instance if both latitude and longitude are successfully parsed, otherwise undefined.
   */
  static fromUnparsedObj(obj: {
    latitude: string;
    longitude: string;
  }): Coordinate {
    if (!obj || typeof obj !== "object") {
      throw new Error("Invalid input: expected an object");
    }

    const parsedLat = Number(obj.latitude);
    const parsedLng = Number(obj.longitude);

    Coordinate.validate(parsedLat, parsedLng);

    return new Coordinate({
      latitude: parsedLat,
      longitude: parsedLng,
    });
  }

  /**
   * Creates a new Coordinate instance from a GeoJSON Point object.
   *
   * @param {GeoJSON.Point} obj - The GeoJSON Point object to convert.
   * @return {Coordinate} The newly created Coordinate instance.
   */
  static fromGEOJsonPoint(obj: GeoJSON.Point): Coordinate {
    // guard clauses
    if (!obj || !obj.coordinates || !Array.isArray(obj.coordinates)) {
      throw new Error(
        "Invalid GeoJSON Point object: missing or invalid coordinates",
      );
    }

    if (obj.coordinates.length < 2 || obj.coordinates.length > 3) {
      throw new Error(
        "Invalid GeoJSON Point object: coordinates array should have 2 or 3 elements",
      );
    }

    const [longitude, latitude] = obj.coordinates as [number, number];

    Coordinate.validate(latitude, longitude);

    // create coordinate
    return new Coordinate({
      latitude: (obj.coordinates as [number, number])[1],
      longitude: (obj.coordinates as [number, number])[0],
    });
  }

  /**
   * Creates a new Coordinate instance from an object with Latitude and Longitude properties.
   *
   * @param {Object} obj - An object containing Latitude and Longitude as numbers.
   * @return {Coordinate} The newly created Coordinate instance.
   */
  static fromCapitalizedObj(obj: { Latitude: number; Longitude: number }) {
    // guard clauses
    if (obj.Latitude === undefined || obj.Longitude === undefined)
      throw new Error("Invalid coordinates, missing latitude or longitude");
    if (typeof obj.Latitude !== "number" || typeof obj.Longitude !== "number")
      throw new Error(
        "Invalid coordinates, latitude and longitude must be numbers",
      );

    Coordinate.validate(obj.Latitude, obj.Longitude);

    // create coordinate
    return new Coordinate({
      latitude: obj.Latitude,
      longitude: obj.Longitude,
    });
  }

  // -------------------------------------------------------------------------- //
  // --------------------------------- Getters -------------------------------- //
  // -------------------------------------------------------------------------- //

  /**
   * Tests if the coordinate is with in the bounds of earth and not the default null island.
   *
   * @returns true if is a valid coordinate.
   */
  public get isValid(): boolean {
    if (this.latitude === 0 && this.longitude === 0) return false; // null island is a default not a drivable location.
    return true;
  }

  /**
   * Converts to leaflet supported [lat,lng] format.
   *
   * @returns a latitude longitude point array
   */
  public get asLatLng(): [number, number] {
    return [this.latitude, this.longitude];
  }

  /**
   * Converts to haversine supported [lng,lat] format.
   *
   * @returns a longitude latitude point array
   */
  public get asLngLat(): [number, number] {
    return [this.longitude, this.latitude];
  }

  /**
   * Converts to Valhalla supported lat lon obj format.
   *
   * @returns an obj containing a lat and lon properties.
   */
  public get asAbbreviatedObj(): { lat: number; lon: number } {
    return { lat: this.latitude, lon: this.longitude };
  }

  /**
   * Converts to EV Nav supported Latitude Longitude obj format.
   *
   * @returns an obj containing a Latitude and Longitude properties.
   */
  public get asCapitalizedObj(): { Latitude: number; Longitude: number } {
    return { Latitude: this.latitude, Longitude: this.longitude };
  }

  // -------------------------------------------------------------------------- //
  // --------------------------------- Methods -------------------------------- //
  // -------------------------------------------------------------------------- //

  /**
   * Returns true if this coordinate represents the same location
   * as the supplied coordinate.
   *
   * @param coordinate Coordinate to compare against.
   */
  public isSameLocation(coordinate: Coordinate): boolean {
    return (
      this.latitude === coordinate.latitude &&
      this.longitude === coordinate.longitude
    );
  }
}
